import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Layout } from "@/components/Layout";
import { ImageDropZone } from "@/components/ImageDropZone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Edit, Trash2, Map, Upload, ZoomIn, ZoomOut, Move, Pen,
  MapPin, Castle, Trees, Mountain, Landmark, Home, Skull,
  Church, Tent, Anchor, Flame, X, Check, Search, UserCircle2, FolderOpen, FolderPlus
} from "lucide-react";
import VisibilityToggle from "@/components/VisibilityToggle";
import EntityContextMenu from "@/components/EntityContextMenu";
import { FolderRail, FolderToolbar, FolderCard, partitionCurrentLevel, useFolderCrud } from "@/components/FolderGrid";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Location type icons and colors
const LOCATION_TYPES = {
  city:      { icon: Castle,   color: "#d4af37", label: "City" },
  town:      { icon: Home,     color: "#c9a84c", label: "Town" },
  village:   { icon: Tent,     color: "#8b7355", label: "Village" },
  dungeon:   { icon: Skull,    color: "#dc2626", label: "Dungeon" },
  forest:    { icon: Trees,    color: "#16a34a", label: "Forest" },
  mountain:  { icon: Mountain, color: "#94a3b8", label: "Mountain" },
  temple:    { icon: Church,   color: "#a78bfa", label: "Temple" },
  ruins:     { icon: Landmark, color: "#78716c", label: "Ruins" },
  port:      { icon: Anchor,   color: "#3b82f6", label: "Port" },
  tavern:    { icon: Flame,    color: "#f59e0b", label: "Tavern" },
  other:     { icon: MapPin,   color: "#9ca3af", label: "Other" },
};

const PinIcon = ({ type, size = 24, active = false }) => {
  const config = LOCATION_TYPES[type] || LOCATION_TYPES.other;
  const Icon = config.icon;
  return (
    <div
      className={`relative flex items-center justify-center transition-transform ${active ? "scale-125" : "hover:scale-110"}`}
      style={{ width: size + 12, height: size + 16 }}
    >
      {/* Pin shape */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-3 rounded-b-full"
        style={{ backgroundColor: config.color, opacity: 0.7 }}
      />
      <div
        className={`relative flex items-center justify-center rounded-full border-2 ${active ? "ring-2 ring-white/50" : ""}`}
        style={{
          width: size + 8,
          height: size + 8,
          backgroundColor: `${config.color}22`,
          borderColor: config.color,
        }}
      >
        <Icon style={{ width: size * 0.6, height: size * 0.6, color: config.color }} />
      </div>
    </div>
  );
};

const MapsPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [maps, setMaps] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selectedMap, setSelectedMap] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [mapImageUrl, setMapImageUrl] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "", folder_id: null });
  const [folders, setFolders] = useState([]);
  const folderCrud = useFolderCrud({ entityType: "maps", onRefresh: () => fetchData() });
  // Keep local folders state in sync with the hook's
  useEffect(() => { setFolders(folderCrud.folders); }, [folderCrud.folders]);

  // Pin state
  const [pinMode, setPinMode] = useState(false);
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pendingPin, setPendingPin] = useState(null); // {x, y} of click
  const [pinAction, setPinAction] = useState("link"); // "link" or "create"
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [newLocationData, setNewLocationData] = useState({ name: "", description: "", location_type: "city" });
  const [locationSearch, setLocationSearch] = useState("");
  const [activePin, setActivePin] = useState(null);
  const [savingPin, setSavingPin] = useState(false);
  const mapImageRef = useRef(null);
  const transformRef = useRef(null);
  const canvasContainerRef = useRef(null);
  // Tracks whether the current map has been auto-fitted yet. We block panning
  // until the first fit happens to prevent a flash of the un-fitted (native
  // size) image being visible/draggable in the corner.
  const [hasFitted, setHasFitted] = useState(false);

  // Single source of truth: read CURRENT actual DOM sizes (container client +
  // image natural) and apply the centred fit transform via setTransform. This
  // is fully reactive — it doesn't matter when this is called (mount, image
  // load, container resize, button click), it always uses the latest sizes.
  const applyFit = (animate = false) => {
    const img = mapImageRef.current;
    const wrapper = transformRef.current;
    const container = canvasContainerRef.current;
    if (!img || !wrapper || !container) return false;
    // Defensive clamp: trust the SMALLEST of container.clientWidth, its parent's
    // clientWidth, and (viewport - sidebar). This prevents a corrupted-by-content
    // ancestor flex-chain from polluting our fit target. After the Layout.jsx
    // min-w-0 fix this is belt-and-braces — but it means even a future page that
    // bypasses the layout safeguards still gets a correctly-fitted map.
    const containerCw = container.clientWidth || 0;
    const parentCw = container.parentElement?.clientWidth || containerCw;
    const viewportCw = Math.max(0, window.innerWidth - 280); // 280 ≈ sidebar
    const cw = Math.min(
      ...[containerCw, parentCw, viewportCw].filter(v => v > 0)
    ) || containerCw;
    const ch = container.clientHeight;
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    // If anything is still 0, bail — we'll be called again as soon as the
    // resize observer / image load fires with real numbers.
    if (!cw || !ch || !iw || !ih) return false;
    const s = Math.min(cw / iw, ch / ih) * 0.95;
    const safe = Math.max(0.02, Math.min(s, 4));
    const x = (cw - iw * safe) / 2;
    const y = (ch - ih * safe) / 2;
    if (typeof wrapper.setTransform === "function") {
      wrapper.setTransform(x, y, safe, animate ? 200 : 0);
      setHasFitted(true);
      return true;
    }
    return false;
  };

  // Imperative re-fit alias for the toolbar button + window resize listener.
  const fitMapToView = () => applyFit(true);

  useEffect(() => {
    fetchData();
  }, [campaignId]);

  useEffect(() => {
    if (selectedMap?.storage_path) {
      fetchMapImage(selectedMap.storage_path);
    } else if (selectedMap?.image_path) {
      setMapImageUrl(selectedMap.image_path);
    } else {
      setMapImageUrl(null);
    }
    setActivePin(null);
    setHasFitted(false);
  }, [selectedMap]);

  // Safety net: if for any reason the fit never lands within 600ms of a new
  // map being selected (e.g. image fails to load, container stays 0×0), force
  // the image visible anyway so the user is never stuck on a blank canvas.
  // This is the ultimate fallback — the normal path (onLoad → applyFit) is
  // expected to succeed within a single animation frame.
  useEffect(() => {
    if (!mapImageUrl || hasFitted) return;
    const timer = setTimeout(() => {
      if (!applyFit(false)) {
        // Still couldn't compute a fit — just unhide the image so the user
        // can at least see and pan/zoom manually.
        setHasFitted(true);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [mapImageUrl, hasFitted]);

  // ResizeObserver on the canvas container — re-fit whenever the container's
  // own size changes (sidebar toggles, browser resize, layout settling on
  // first paint). This is what eliminates the production race: even if the
  // image loads BEFORE the flex container has its final size, the observer
  // will fire when the container settles and we re-fit then.
  useEffect(() => {
    const container = canvasContainerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      // Only re-fit automatically while the user hasn't yet interacted with
      // a fitted map. Once they've zoomed/panned we don't want to yank the
      // view from under them on every container resize. The toolbar Fit-to-
      // View button is always available for an explicit re-fit.
      if (!hasFitted) applyFit(false);
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [mapImageUrl, hasFitted]);

  // Re-fit on window resize so the map stays cleanly framed even after the
  // first fit (debounced via rAF).
  useEffect(() => {
    let raf = null;
    const onResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => fitMapToView());
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const fetchData = async () => {
    try {
      const [campaignRes, mapsRes, locRes] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/maps`, { withCredentials: true }),
        axios.get(`${API}/campaigns/${campaignId}/locations`, { withCredentials: true }),
      ]);
      setCampaign(campaignRes.data);
      setMaps(mapsRes.data);
      setLocations(locRes.data);
      folderCrud.refresh();
      if (mapsRes.data.length > 0 && !selectedMap) {
        setSelectedMap(mapsRes.data[0]);
      } else if (selectedMap) {
        const updated = mapsRes.data.find(m => m.id === selectedMap.id);
        if (updated) setSelectedMap(updated);
      }
    } catch (error) {
      toast.error("Failed to load maps");
    } finally {
      setLoading(false);
    }
  };

  const fetchMapImage = async (storagePath) => {
    try {
      const response = await axios.get(`${API}/files/${storagePath}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const blobUrl = URL.createObjectURL(response.data);
      setMapImageUrl(blobUrl);
    } catch (error) {
      setMapImageUrl(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, campaign_id: campaignId };
      if (editing) {
        await axios.put(`${API}/maps/${editing.id}`, payload, { withCredentials: true });
        toast.success("Map updated");
      } else {
        await axios.post(`${API}/maps`, payload, { withCredentials: true });
        toast.success("Map created");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save map");
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm("Delete Map", "Are you sure you want to delete this map?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/maps/${id}`, { withCredentials: true });
      toast.success("Map deleted");
      if (selectedMap?.id === id) {
        setSelectedMap(null);
        setMapImageUrl(null);
      }
      fetchData();
    } catch (error) {
      toast.error("Failed to delete map");
    }
  };

  const handleUpload = async (file) => {
    if (!file || !selectedMap) return;
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      await axios.post(`${API}/maps/${selectedMap.id}/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Map image uploaded");
      fetchData();
      const mapRes = await axios.get(`${API}/maps/${selectedMap.id}`, { withCredentials: true });
      setSelectedMap(mapRes.data);
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({ name: "", description: "", folder_id: null });
  };

  const openEdit = (map) => {
    setEditing(map);
    setFormData({ name: map.name, description: map.description || "", folder_id: map.folder_id || null });
    setDialogOpen(true);
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  // --- Pin handlers ---

  const handleMapClick = (e) => {
    if (!pinMode || !mapImageRef.current) return;
    const rect = mapImageRef.current.getBoundingClientRect();
    const imgEl = mapImageRef.current;
    // Calculate percentage position relative to the image
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPendingPin({ x, y });
    setPinAction("link");
    setSelectedLocationId("");
    setNewLocationData({ name: "", description: "", location_type: "city" });
    setLocationSearch("");
    setPinDialogOpen(true);
  };

  const handleSavePin = async () => {
    if (!selectedMap || !pendingPin) return;
    setSavingPin(true);
    try {
      const payload = {
        x: pendingPin.x,
        y: pendingPin.y,
      };
      if (pinAction === "create") {
        payload.create_location = true;
        payload.location_name = newLocationData.name;
        payload.location_description = newLocationData.description;
        payload.location_type = newLocationData.location_type;
      } else {
        payload.location_id = selectedLocationId || null;
        if (selectedLocationId) {
          const loc = locations.find(l => l.id === selectedLocationId);
          if (loc) {
            payload.location_type = loc.location_type;
            payload.label = loc.name;
          }
        }
      }
      await axios.post(`${API}/maps/${selectedMap.id}/pins`, payload, { withCredentials: true });
      toast.success("Pin added!");
      setPinDialogOpen(false);
      setPendingPin(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add pin");
    } finally {
      setSavingPin(false);
    }
  };

  const handleDeletePin = async (pinId) => {
    const ok = await confirm("Remove Pin", "Remove this pin from the map?");
    if (!ok) return;
    try {
      await axios.delete(`${API}/maps/${selectedMap.id}/pins/${pinId}`, { withCredentials: true });
      toast.success("Pin removed");
      setActivePin(null);
      fetchData();
    } catch (err) {
      toast.error("Failed to remove pin");
    }
  };

  const handlePinClick = (pin, e) => {
    e.stopPropagation();
    if (activePin?.id === pin.id) {
      // Second click — close panel
      closeQuickRef();
    } else {
      openQuickRef(pin);
    }
  };

  // --- Quick Reference Panel ---
  const [quickRefLocation, setQuickRefLocation] = useState(null);
  const [quickRefBuildings, setQuickRefBuildings] = useState([]);
  const [quickRefNpcs, setQuickRefNpcs] = useState([]);
  const [loadingQuickRef, setLoadingQuickRef] = useState(false);

  const openQuickRef = async (pin) => {
    setActivePin(pin);
    if (!pin.location_id) {
      // No linked location — just show pin info panel with delete option
      setQuickRefLocation(null);
      setQuickRefBuildings([]);
      setQuickRefNpcs([]);
      setLoadingQuickRef(false);
      return;
    }
    setLoadingQuickRef(true);
    try {
      const [locRes, bldgRes, npcRes] = await Promise.all([
        axios.get(`${API}/locations/${pin.location_id}`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/locations/${pin.location_id}/buildings`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/campaigns/${campaignId}/npcs`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setQuickRefLocation(locRes.data);
      setQuickRefBuildings(bldgRes.data || []);
      const linkedNpcs = (npcRes.data || []).filter(n => n.location_id === pin.location_id);
      setQuickRefNpcs(linkedNpcs);
    } catch {
      setQuickRefLocation(null);
    } finally {
      setLoadingQuickRef(false);
    }
  };

  const closeQuickRef = () => {
    setQuickRefLocation(null);
    setQuickRefBuildings([]);
    setQuickRefNpcs([]);
    setActivePin(null);
  };

  const pins = selectedMap?.pins || [];
  const filteredLocations = locations.filter(l =>
    l.name.toLowerCase().includes(locationSearch.toLowerCase()) &&
    !pins.some(p => p.location_id === l.id)
  );

  if (loading) {
    return (
      <Layout campaignName="Loading...">
        <div className="flex justify-center py-20"><div className="spinner"></div></div>
      </Layout>
    );
  }

  return (
    <Layout campaignName={campaign?.name} tabLabel="World Atlas">
      <div data-testid="maps-page">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif text-foreground">World Atlas</h1>
            <p className="text-muted-foreground text-sm">Upload and explore your campaign maps</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate(`/campaign/${campaignId}/maps/draw`)}
              data-testid="draw-map-button"
              className="rounded-sm border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
            >
              <Pen className="w-4 h-4 mr-2" /> Draw Map
            </Button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={openCreate} data-testid="create-map-button" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
                  <Plus className="w-4 h-4 mr-2" /> New Map
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">{editing ? "Edit Map" : "New Map"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Name</label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="World Map" required data-testid="map-name-input" className="bg-input border-border rounded-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Description</label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="A map of the known world..." rows={3} data-testid="map-description-input" className="bg-input border-border rounded-sm" />
                </div>
                {folders.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">Folder</label>
                    <Select value={formData.folder_id || "none"} onValueChange={(v) => setFormData({ ...formData, folder_id: v === "none" ? null : v })}>
                      <SelectTrigger className="bg-input border-border rounded-sm" data-testid="map-folder-select"><SelectValue placeholder="No folder" /></SelectTrigger>
                      <SelectContent className="bg-popover border-border">
                        <SelectItem value="none">No folder</SelectItem>
                        {folders.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button type="submit" data-testid="save-map-button" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
                  {editing ? "Save Changes" : "Create Map"}
                </Button>
              </form>
            </DialogContent>
            </Dialog>
          </div>
        </div>

        {maps.length === 0 ? (
          <div className="empty-state">
            <Map className="empty-state-icon text-muted-foreground" />
            <h3 className="text-xl font-serif text-foreground mb-2">No maps yet</h3>
            <p className="text-muted-foreground mb-6">Upload maps of your world</p>
            <Button onClick={openCreate} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm">
              <Plus className="w-4 h-4 mr-2" /> Create Map
            </Button>
          </div>
        ) : (
          <div className="flex gap-2 h-[calc(100vh-160px)]">
            {/* VTT-style: compact tab strip down the left, map fills the rest */}
            <div className="w-56 shrink-0 flex flex-col overflow-hidden bg-card/40 border border-border rounded-sm">
              {/* Folder breadcrumb / picker, compact */}
              <div className="px-2 py-2 border-b border-border/60 flex-shrink-0">
                <FolderToolbar
                  folders={folders}
                  currentFolderId={folderCrud.currentFolderId}
                  onNavigate={folderCrud.setCurrentFolderId}
                  onNewFolder={folderCrud.createHere}
                  allLabel="All Maps"
                  className=""
                />
              </div>
              <div className="overflow-y-auto flex-1 py-1">
                {(() => {
                  const { folders: cf, items: ci } = partitionCurrentLevel({ folders, items: maps, currentFolderId: folderCrud.currentFolderId });
                  return (
                    <>
                      {cf.length > 0 && (
                        <div className="px-2 py-1 space-y-0.5" data-testid="maps-folder-grid">
                          {cf.map(f => (
                            <button
                              key={f.id}
                              type="button"
                              onClick={() => folderCrud.setCurrentFolderId(f.id)}
                              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-sm text-left text-xs text-muted-foreground hover:bg-accent/30 hover:text-foreground transition-colors"
                              data-testid={`map-folder-${f.id}`}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={(e) => { const id = e.dataTransfer.getData("entityId"); if (id) folderCrud.moveItem(id, f.id); }}
                            >
                              <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: f.color || "#888" }} />
                              <span className="truncate flex-1">{f.name}</span>
                              <span className="text-[10px] opacity-60">{maps.filter(m => m.folder_id === f.id).length}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {cf.length > 0 && ci.length > 0 && (
                        <div className="border-t border-border/40 my-1" aria-hidden="true" />
                      )}
                      <div className="px-1 space-y-px">
                        {ci.map((map) => {
                          const folderColor = map.folder_id ? folders.find(f => f.id === map.folder_id)?.color : null;
                          const isActive = selectedMap?.id === map.id;
                          return (
                            <EntityContextMenu
                              key={map.id}
                              onOpen={() => { setSelectedMap(map); closeQuickRef(); }}
                              onRename={() => openEdit(map)}
                              onDelete={() => handleDelete(map.id)}
                              folders={folders}
                              currentFolderId={map.folder_id}
                              onMoveFolder={(fid) => folderCrud.moveItem(map.id, fid)}
                              pinPath="maps"
                              pinLabel={`Map: ${map.name}`}
                              entityName={map.name}
                            >
                              <div
                                className={`group relative w-full flex items-center gap-2 pl-3 pr-2 py-1.5 text-left transition-all cursor-pointer overflow-hidden border-l-2 ${
                                  isActive
                                    ? 'bg-primary/10 border-primary text-foreground'
                                    : 'bg-transparent border-transparent text-muted-foreground hover:bg-accent/30 hover:text-foreground hover:border-primary/30'
                                }`}
                                onClick={() => { setSelectedMap(map); closeQuickRef(); }}
                                draggable
                                onDragStart={(e) => e.dataTransfer.setData("entityId", map.id)}
                                data-testid={`map-item-${map.id}`}
                                title={folderColor ? folders.find(f => f.id === map.folder_id)?.name || "" : undefined}
                              >
                                {folderColor && (
                                  <span className="absolute left-0 top-1 bottom-1 w-0.5 rounded-r" style={{ backgroundColor: folderColor }} aria-hidden="true" />
                                )}
                                <Map className="w-3.5 h-3.5 shrink-0 opacity-70" />
                                <span className="text-xs font-medium truncate flex-1">{map.name}</span>
                                {map.pins?.length > 0 && (
                                  <span className="text-[9px] text-muted-foreground/70 tabular-nums">{map.pins.length}</span>
                                )}
                                <div className="hidden group-hover:flex gap-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                                  <VisibilityToggle entityType="maps" entityId={map.id} currentVisibility={map.visibility || "dm_only"} onUpdate={() => fetchData()} />
                                  <button className="p-0.5 hover:text-primary transition-colors" onClick={() => openEdit(map)} data-testid={`edit-map-${map.id}`}><Edit className="w-3 h-3" /></button>
                                  <button className="p-0.5 hover:text-destructive transition-colors" onClick={() => handleDelete(map.id)} data-testid={`delete-map-${map.id}`}><Trash2 className="w-3 h-3" /></button>
                                </div>
                              </div>
                            </EntityContextMenu>
                          );
                        })}
                      </div>
                      {cf.length === 0 && ci.length === 0 && (
                        <div className="text-center py-12 text-muted-foreground/70 text-xs px-4" data-testid="maps-empty">
                          {folderCrud.currentFolderId === "__archived__" ? "No archived folders." : "Empty. Create a map or sub-folder."}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Map viewer — takes remaining space */}
            <div className="flex-1 min-w-0 flex">
              <div className="flex-1 min-w-0">
                {selectedMap ? (
                  <div className="h-full flex flex-col bg-card border border-border rounded-sm">
                    {/* Toolbar */}
                    <div className="flex justify-between items-center px-4 py-2.5 border-b border-border shrink-0">
                      <div className="flex items-center gap-3 min-w-0">
                        <h2 className="text-lg font-serif text-foreground truncate">{selectedMap.name}</h2>
                        {pins.length > 0 && (
                          <div className="hidden md:flex flex-wrap gap-1.5">
                            {pins.slice(0, 5).map(pin => {
                              const config = LOCATION_TYPES[pin.location_type] || LOCATION_TYPES.other;
                              const Icon = config.icon;
                              return (
                                <button
                                  key={pin.id}
                                  onClick={() => openQuickRef(pin)}
                                  className={`flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] border transition-colors ${
                                    activePin?.id === pin.id ? "border-primary bg-primary/10" : "border-border/40 hover:border-border"
                                  }`}
                                  data-testid={`pin-legend-${pin.id}`}
                                >
                                  <Icon className="w-2.5 h-2.5" style={{ color: config.color }} />
                                  {pin.label || "Unnamed"}
                                </button>
                              );
                            })}
                            {pins.length > 5 && <span className="text-[10px] text-muted-foreground self-center">+{pins.length - 5} more</span>}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant={pinMode ? "default" : "outline"}
                          size="sm"
                          onClick={() => { setPinMode(!pinMode); closeQuickRef(); }}
                          data-testid="toggle-pin-mode"
                          className={`h-8 text-xs ${pinMode ? "bg-amber-600 hover:bg-amber-700 text-white" : "border-border text-foreground"}`}
                        >
                          <MapPin className="w-3.5 h-3.5 mr-1.5" />
                          {pinMode ? "Placing..." : "Add Pins"}
                        </Button>
                        <ImageDropZone
                          compact
                          onFileSelect={handleUpload}
                          testId="upload-map-image-button"
                          label={uploading ? "Uploading..." : "Upload"}
                        />
                      </div>
                    </div>

                    {/* Map canvas */}
                    <div ref={canvasContainerRef} className={`map-canvas-container flex-1 bg-muted/10 overflow-hidden relative ${pinMode ? "cursor-crosshair" : ""}`}>
                      {mapImageUrl ? (
                        <TransformWrapper
                          key={mapImageUrl}
                          ref={transformRef}
                          initialScale={1}
                          initialPositionX={0}
                          initialPositionY={0}
                          minScale={0.02}
                          maxScale={8}
                          centerOnInit={false}
                          limitToBounds={false}
                          panning={{ disabled: pinMode || !hasFitted }}
                          wheel={{ disabled: !hasFitted }}
                        >
                          {({ zoomIn, zoomOut }) => (
                            <>
                              <div className="absolute top-2 right-2 z-10 flex gap-1.5">
                                {!pinMode && (
                                  <>
                                    <Button size="icon" variant="secondary" onClick={() => zoomIn()} className="h-7 w-7 bg-card/80 backdrop-blur-sm" title="Zoom In"><ZoomIn className="w-3.5 h-3.5" /></Button>
                                    <Button size="icon" variant="secondary" onClick={() => zoomOut()} className="h-7 w-7 bg-card/80 backdrop-blur-sm" title="Zoom Out"><ZoomOut className="w-3.5 h-3.5" /></Button>
                                    <Button size="icon" variant="secondary" onClick={fitMapToView} className="h-7 w-7 bg-card/80 backdrop-blur-sm" title="Fit to View"><Move className="w-3.5 h-3.5" /></Button>
                                  </>
                                )}
                              </div>
                              {pinMode && (
                                <div className="absolute top-2 left-2 z-10 bg-amber-600/90 text-white text-xs px-3 py-1.5 rounded-sm pointer-events-none">
                                  Click on the map to place a pin
                                </div>
                              )}
                              <TransformComponent
                                wrapperStyle={{ width: "100%", height: "100%" }}
                                contentStyle={{ position: "relative" }}
                              >
                                <div className="relative" style={{ display: "inline-block" }}>
                                  <img
                                    ref={mapImageRef}
                                    src={mapImageUrl}
                                    alt={selectedMap.name}
                                    style={{ maxWidth: "none", display: "block", visibility: hasFitted ? "visible" : "hidden" }}
                                    draggable={false}
                                    onLoad={() => {
                                      // Apply fit using the actually-rendered img's natural dims
                                      // and the actually-laid-out container size. If the container
                                      // isn't fully sized yet, the ResizeObserver effect will fire
                                      // again as soon as it is and we'll fit then.
                                      // Use rAF to ensure the browser has committed the layout
                                      // before we measure (critical on production where the layout
                                      // can settle in the same paint as the image decode).
                                      requestAnimationFrame(() => {
                                        if (!applyFit(false)) {
                                          // Try once more on the next frame — handles the rare case
                                          // where the container is still 0×0 on the first attempt.
                                          requestAnimationFrame(() => applyFit(false));
                                        }
                                      });
                                    }}
                                  />
                                  {pinMode && (
                                    <div
                                      className="absolute inset-0 cursor-crosshair"
                                      style={{ zIndex: 5 }}
                                      onClick={(e) => {
                                        if (!mapImageRef.current) return;
                                        const rect = mapImageRef.current.getBoundingClientRect();
                                        const x = ((e.clientX - rect.left) / rect.width) * 100;
                                        const y = ((e.clientY - rect.top) / rect.height) * 100;
                                        setPendingPin({ x, y });
                                        setPinAction("link");
                                        setSelectedLocationId("");
                                        setNewLocationData({ name: "", description: "", location_type: "city" });
                                        setLocationSearch("");
                                        setPinDialogOpen(true);
                                      }}
                                      data-testid="pin-overlay"
                                    />
                                  )}
                                  {pins.map(pin => (
                                    <div
                                      key={pin.id}
                                      className="absolute -translate-x-1/2 -translate-y-full"
                                      style={{
                                        left: `${pin.x}%`,
                                        top: `${pin.y}%`,
                                        zIndex: activePin?.id === pin.id ? 20 : 10,
                                      }}
                                      onClick={(e) => handlePinClick(pin, e)}
                                      data-testid={`map-pin-${pin.id}`}
                                    >
                                      <PinIcon
                                        type={pin.location_type}
                                        size={activePin?.id === pin.id ? 28 : 22}
                                        active={activePin?.id === pin.id}
                                      />
                                      {/* Minimal label on hover */}
                                      {activePin?.id === pin.id && !quickRefLocation && (
                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 bg-card border border-border rounded-sm px-2 py-1 shadow-lg whitespace-nowrap z-30">
                                          <p className="text-xs font-medium">{pin.label || "Unnamed"}</p>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </TransformComponent>
                            </>
                          )}
                        </TransformWrapper>
                      ) : (
                        <ImageDropZone
                          onFileSelect={handleUpload}
                          testId="map-drop-zone"
                          label="Drag & drop a map image here, or click to browse"
                          className="h-full min-h-[400px]"
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-muted-foreground border border-dashed border-border/50 rounded-sm">
                    <div className="text-center">
                      <Map className="w-10 h-10 mx-auto mb-3 opacity-40" />
                      <p className="text-sm">Select a map to view</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Quick Reference Panel */}
              {activePin && (
                <div className="w-80 shrink-0 ml-3 h-full overflow-y-auto bg-card border border-border rounded-sm animate-in slide-in-from-right-5 duration-200" data-testid="quick-ref-panel">
                  <div className="sticky top-0 z-10 bg-card border-b border-border px-4 py-3 flex items-center justify-between">
                    <h3 className="font-serif text-base text-foreground truncate">{quickRefLocation?.name || activePin.label || "Unnamed Pin"}</h3>
                    <button onClick={closeQuickRef} className="p-1 hover:text-foreground text-muted-foreground transition-colors" data-testid="close-quick-ref">
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {loadingQuickRef ? (
                    <div className="flex justify-center py-10"><div className="spinner"></div></div>
                  ) : (
                    <div className="p-4 space-y-4">
                      {quickRefLocation ? (
                        <>
                          {/* Location image */}
                          {quickRefLocation.image_path && (
                            <div className="rounded-sm overflow-hidden border border-border">
                              <img
                                src={quickRefLocation.image_path.startsWith("http") ? quickRefLocation.image_path : `${API}/files/${quickRefLocation.image_path}`}
                                alt={quickRefLocation.name}
                                className="w-full h-36 object-cover"
                                {...(!quickRefLocation.image_path.startsWith("http") && { crossOrigin: "use-credentials" })}
                              />
                            </div>
                          )}

                          {/* Type badge */}
                          <div className="flex items-center gap-2">
                            {(() => {
                              const config = LOCATION_TYPES[quickRefLocation.location_type?.toLowerCase()] || LOCATION_TYPES.other;
                              const Icon = config.icon;
                              return (
                                <span className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-sm border border-border/50" style={{ color: config.color }}>
                                  <Icon className="w-3 h-3" /> {config.label || quickRefLocation.location_type}
                                </span>
                              );
                            })()}
                          </div>

                          {/* Description */}
                          {quickRefLocation.description && (
                            <div className="text-sm text-muted-foreground leading-relaxed prose-sm max-h-40 overflow-y-auto" dangerouslySetInnerHTML={{ __html: quickRefLocation.description }} />
                          )}

                          {/* Buildings */}
                          {quickRefBuildings.length > 0 && (
                            <div>
                              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-2">Buildings ({quickRefBuildings.length})</h4>
                              <div className="space-y-1.5">
                                {quickRefBuildings.map(b => (
                                  <div key={b.id} className="flex items-center gap-2 px-2.5 py-2 bg-muted/20 rounded-sm border border-border/30">
                                    <Home className="w-3.5 h-3.5 text-primary shrink-0" />
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium truncate">{b.name}</p>
                                      {b.building_type && <p className="text-[10px] text-muted-foreground capitalize">{b.building_type}</p>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* NPCs */}
                          {quickRefNpcs.length > 0 && (
                            <div>
                              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-2">NPCs ({quickRefNpcs.length})</h4>
                              <div className="space-y-1.5">
                                {quickRefNpcs.map(n => (
                                  <div key={n.id} className="flex items-center gap-2 px-2.5 py-2 bg-muted/20 rounded-sm border border-border/30">
                                    <UserCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium truncate">{n.name}</p>
                                      {n.profession && <p className="text-[10px] text-muted-foreground">{n.profession}</p>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Go to Location button */}
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full mt-2 border-primary/30 text-primary hover:bg-primary/10"
                            onClick={() => navigate(`/campaign/${campaignId}/locations`)}
                            data-testid="quick-ref-go-to-location"
                          >
                            <Castle className="w-3.5 h-3.5 mr-2" /> View Full Location
                          </Button>
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <div className="flex items-center justify-center gap-2 mb-2">
                            {(() => {
                              const config = LOCATION_TYPES[activePin.location_type?.toLowerCase()] || LOCATION_TYPES.other;
                              const Icon = config.icon;
                              return <Icon className="w-5 h-5" style={{ color: config.color }} />;
                            })()}
                            <span className="text-sm font-medium text-foreground capitalize">{activePin.location_type || "Pin"}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">No linked location data</p>
                        </div>
                      )}

                      {/* Delete pin — always available */}
                      <button
                        onClick={() => { handleDeletePin(activePin.id); closeQuickRef(); }}
                        className="w-full text-xs text-destructive/70 hover:text-destructive text-center py-2 border border-destructive/20 rounded-sm hover:bg-destructive/5 transition-colors"
                        data-testid="quick-ref-delete-pin"
                      >
                        Remove Pin from Map
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pin placement dialog */}
        <Dialog open={pinDialogOpen} onOpenChange={(open) => { if (!open) { setPinDialogOpen(false); setPendingPin(null); } }}>
          <DialogContent className="bg-card border-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Place a Pin</DialogTitle>
            </DialogHeader>

            <div className="flex bg-muted/30 rounded-sm p-1 mb-4">
              <button
                onClick={() => setPinAction("link")}
                className={`flex-1 py-2 text-sm rounded-sm transition-colors ${pinAction === "link" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="pin-action-link"
              >
                Link Existing Location
              </button>
              <button
                onClick={() => setPinAction("create")}
                className={`flex-1 py-2 text-sm rounded-sm transition-colors ${pinAction === "create" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid="pin-action-create"
              >
                Create New Location
              </button>
            </div>

            {pinAction === "link" ? (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search locations..."
                    value={locationSearch}
                    onChange={(e) => setLocationSearch(e.target.value)}
                    className="pl-9"
                    data-testid="pin-location-search"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1" data-testid="pin-location-list">
                  {filteredLocations.map(loc => {
                    const config = LOCATION_TYPES[loc.location_type] || LOCATION_TYPES.other;
                    const Icon = config.icon;
                    return (
                      <button
                        key={loc.id}
                        onClick={() => setSelectedLocationId(loc.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-sm text-sm text-left transition-colors ${
                          selectedLocationId === loc.id ? "bg-primary/10 border border-primary/30" : "hover:bg-muted/30 border border-transparent"
                        }`}
                        data-testid={`pin-select-location-${loc.id}`}
                      >
                        <Icon className="w-4 h-4 shrink-0" style={{ color: config.color }} />
                        <div>
                          <p className="font-medium">{loc.name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{loc.location_type}</p>
                        </div>
                        {selectedLocationId === loc.id && <Check className="w-4 h-4 text-primary ml-auto" />}
                      </button>
                    );
                  })}
                  {filteredLocations.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      {locations.length === 0 ? "No locations created yet" : "No matching locations"}
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Location Name</label>
                  <Input
                    value={newLocationData.name}
                    onChange={(e) => setNewLocationData({ ...newLocationData, name: e.target.value })}
                    placeholder="Ravenhollow Keep"
                    required
                    data-testid="pin-new-location-name"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Type</label>
                  <div className="grid grid-cols-4 gap-1.5">
                    {Object.entries(LOCATION_TYPES).map(([key, config]) => {
                      const Icon = config.icon;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setNewLocationData({ ...newLocationData, location_type: key })}
                          className={`flex flex-col items-center gap-1 py-2 px-1 rounded-sm border text-xs transition-colors ${
                            newLocationData.location_type === key
                              ? "border-primary bg-primary/10"
                              : "border-border/50 hover:border-border"
                          }`}
                          data-testid={`pin-type-${key}`}
                        >
                          <Icon className="w-4 h-4" style={{ color: config.color }} />
                          <span className="truncate w-full text-center">{config.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">Description (optional)</label>
                  <Textarea
                    value={newLocationData.description}
                    onChange={(e) => setNewLocationData({ ...newLocationData, description: e.target.value })}
                    placeholder="A crumbling fortress on the edge of the Shadowmere..."
                    rows={2}
                    data-testid="pin-new-location-desc"
                  />
                </div>
              </div>
            )}

            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => { setPinDialogOpen(false); setPendingPin(null); }} className="flex-1" data-testid="pin-cancel">
                Cancel
              </Button>
              <Button
                onClick={handleSavePin}
                disabled={savingPin || (pinAction === "link" && !selectedLocationId) || (pinAction === "create" && !newLocationData.name)}
                className="flex-1"
                data-testid="pin-save"
              >
                {savingPin ? "Saving..." : "Place Pin"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default MapsPage;
